import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core';
import { closestCenter, DndContext, DragOverlay } from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Plus, Trash01, XClose } from '@untitledui/icons';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button } from 'react-aria-components';

import type { SelectSize } from '@/lib/base/forms/select/select-shared';
import { GripVertical } from '@/lib/base/foundations/grip-vertical';
import type { PropertyTypeSchema } from '@/lib/crm/hooks/usePropertySchema';
import { useMultiValueSensors } from '@/lib/crm/utils/multiValueDnd';
import {
  formatValueForText,
  validateNewValue,
} from '@/lib/crm/utils/multiValueValidation';
import { cx } from '@/utils/cx';

import { AssociationTypeSelectInput } from './inputs/AssociationTypeSelectInput';
import { BookingQuestionsInput } from './inputs/BookingQuestionsInput';
import { ColorSelectInput } from './inputs/ColorSelectInput';
import { CompositeMultiValueEditor } from './inputs/CompositeMultiValueEditor';
import { CurrencyInput } from './inputs/CurrencyInput';
import { DateInput } from './inputs/DateInput';
import { DateTimeInput } from './inputs/DateTimeInput';
import { DomainInput } from './inputs/DomainInput';
import { EmailInput } from './inputs/EmailInput';
import { EntityTypeSelectInput } from './inputs/EntityTypeSelectInput';
import { FieldTypeSelectInput } from './inputs/FieldTypeSelectInput';
import { IconSelectInput } from './inputs/IconSelectInput';
import { LanguageInput } from './inputs/LanguageInput';
import { LocationTypeInput } from './inputs/LocationTypeInput';
import { MarkdownInput } from './inputs/MarkdownInput';
import { MultiCurrencyInput } from './inputs/MultiCurrencyInput';
import { MultiSelectInput } from './inputs/MultiSelectInput';
import { NumberInput } from './inputs/NumberInput';
import { PhoneInput } from './inputs/PhoneInput';
import { PropertyTypeSelectInput } from './inputs/PropertyTypeSelectInput';
import { SelectInput } from './inputs/SelectInput';
import { TextAreaInput } from './inputs/TextAreaInput';
import { TextInput } from './inputs/TextInput';
import { TimezoneInput } from './inputs/TimezoneInput';
import { UrlInput } from './inputs/UrlInput';
import { WorkingHoursInput } from './inputs/WorkingHoursInput';
import { PropertyValueLabel } from './labels/PropertyValueLabel';

export interface PropertyInputProps {
  propertyType: PropertyTypeSchema;
  value: string | number | object | null | undefined;
  onChange: (value: unknown) => void;
  disabled?: boolean;
  /**
   * @deprecated Use CrmPropertyField instead for labeled inputs with edit indicators.
   * This prop is ignored - PropertyInput now renders label-free inputs.
   */
  isEdited?: boolean;
  /** Trailing addon element (only supported for phone_number field type) */
  trailingAddon?: React.ReactNode;
  /** Optional ref to forward to the underlying input element */
  inputRef?: React.RefObject<HTMLInputElement | null>;
  /** Placeholder text for the input field */
  placeholder?: string;
  /**
   * The entity's raw `properties` map (e.g. `object.properties`). Only
   * meaningful when field_type_name is 'formula': forwarded to
   * PropertyValueLabel, which resolves the formula's target entity type
   * (named by a sibling property) and composes its own schema.
   */
  entityProperties?: Record<string, unknown>;
  /**
   * Organization ID, passed through to PropertyValueLabel so uuid-shaped
   * string literals in a formula render as linked entity badges. Only
   * meaningful when field_type_name is 'formula'.
   */
  orgId?: number;
  /**
   * How multi-value properties render. 'inline' (default) shows a compact
   * scalar input with a "+" popover for additional values — suited to forms
   * and record panes. 'card' renders the full value-list editor directly
   * (sortable rows + always-visible add input, auto-focused) — the
   * Attio-style treatment used inside a table cell's floating field, where
   * the host container is already a card. Only meaningful when the property
   * is_multi without subproperties; ignored otherwise.
   */
  multiValueDisplay?: 'inline' | 'card';
  /**
   * Open enumerated pickers (select/boolean dropdowns) and date pickers
   * immediately on mount. Used by table-cell editing, where entering edit
   * mode should drop the user straight into the options — clicking the
   * trigger a second time is pure friction. Ignored by free-text editors.
   */
  autoOpenPicker?: boolean;
  /**
   * Notified when a date/datetime picker popover opens or closes. Lets a host
   * that mounts the input in `autoOpenPicker` mode tear its own inline-edit
   * state down when the popover is dismissed without a selection (Escape /
   * click-away). Ignored by field types that have no popover.
   */
  onPickerOpenChange?: (isOpen: boolean) => void;
  /**
   * How markdown properties render. 'form' (default) shows the standalone
   * rich text field with its own box chrome. 'card' renders the editor
   * chromeless with a compact auto-focused typing area — the treatment for
   * hosts that already provide a floating-card container (a table cell's
   * popped field). 'inline' keeps the form's bordered box but types at the
   * property read view's size with a natural content height — the treatment
   * for InlineEditableFieldRow click-to-edit rows (see MarkdownInput). Only
   * meaningful when field_type_name is 'markdown'; ignored otherwise.
   */
  markdownDisplay?: 'form' | 'card' | 'inline';
  /**
   * Viewport coordinates of the click that opened the editor. With
   * markdownDisplay='inline' the markdown editor drops the caret at the
   * clicked character (read and edit text are pixel-matched). Ignored by
   * other field types and display modes.
   */
  initialCaretPoint?: { x: number; y: number } | null;
  /**
   * Host context for the phone editor. 'form' (default) fills the column so
   * the field matches its neighbours; 'card' keeps the width cap the table
   * cell's popped field sizes itself against. Only meaningful when
   * field_type_name is 'phone_number'; ignored otherwise.
   */
  phoneDisplay?: 'form' | 'card';
  /** Element for text-family leaves (text/org_type_name/web_source). 'form' (default)
   *  = focus-expanding textarea; 'card' = bare <input> for a table cell's popped field
   *  whose HTMLInputElement focus ref + [&_input] chrome are input-bound. */
  textDisplay?: 'form' | 'card';
  /**
   * Field size on the shared library scale (`SelectSize` — the same scale
   * every `lib/base` form control uses), forwarded to whichever adapter this
   * property's field type resolves to. Defaults to 'sm'. Multi-value fields
   * pass it down to each of their rows, so a compact multi-value field stays
   * compact.
   */
  size?: SelectSize;
}

/**
 * Focuses the analogous control for the row at `index` within `container` —
 * preferring that row's remove button (present whenever more than one row is
 * showing) and falling back to its primary input otherwise (e.g. the
 * floor-of-1 implicit row, which never renders a remove button).
 */
function focusRowControl(container: HTMLElement | null, index: number) {
  if (!container) {
    return;
  }
  const row = container.querySelector<HTMLElement>(
    `[data-row-index="${index}"]`
  );
  if (!row) {
    return;
  }
  const removeButton = row.querySelector<HTMLElement>(
    'button[aria-label^="Remove"]'
  );
  if (removeButton) {
    removeButton.focus();
    return;
  }
  focusRowInput(container, index);
}

/**
 * Focuses the primary input of the row at `index`, skipping any remove
 * button. Used when a removal empties the array down to the floor-of-1
 * implicit row — that row's input is the target even if a remove button
 * happens to also render there (e.g. a pending draft row is still open,
 * keeping the row count above 1).
 */
function focusRowInput(container: HTMLElement | null, index: number) {
  if (!container) {
    return;
  }
  const row = container.querySelector<HTMLElement>(
    `[data-row-index="${index}"]`
  );
  row?.querySelector<HTMLElement>('input, select, textarea')?.focus();
}

// =============================================================================
// useMultiValueState - State management hook for multi-value editors
// =============================================================================

/**
 * Manages UI state and event handlers for multi-value field editors.
 *
 * Responsibilities:
 * - UI state: activeId (drag), announcement (a11y), newValue (input), refs, timeouts
 * - UI handlers: drag events, add/remove/change handlers, keyboard navigation
 *
 * Business logic (validation, formatting) is delegated to multiValueValidation.ts.
 * This separation ensures the hook focuses on React state management, not domain rules.
 */
interface UseMultiValueStateReturn {
  valueArray: unknown[];
  newValue: unknown;
  duplicateError: string | null;
  announcement: string;
  activeId: string | null;
  addInputRef: React.RefObject<HTMLInputElement | null>;
  sensors: ReturnType<typeof useMultiValueSensors>;
  handleAdd: () => void;
  handleAddValue: (value: unknown) => void;
  handleRemove: (index: number) => void;
  handleValueChange: (index: number, newVal: unknown) => void;
  handleNewValueChange: (value: unknown) => void;
  handleKeyDown: (e: React.KeyboardEvent) => void;
  handleDragStart: (event: DragStartEvent) => void;
  handleDragEnd: (event: DragEndEvent) => void;
  handleDragCancel: () => void;
}

function useMultiValueState(
  value: unknown,
  onChange: (value: unknown) => void,
  _propertyType: PropertyTypeSchema
): UseMultiValueStateReturn {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [newValue, setNewValue] = useState<unknown>(null);
  const [duplicateError, setDuplicateError] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const addInputRef = useRef<HTMLInputElement>(null);
  const addTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Ensure value is an array
  // Stabilize: prevents infinite re-renders from unstable fallback array identity
  const stableValueArray = useMemo(
    () => (Array.isArray(value) ? value : []),
    [value]
  );

  // Mirrors stableValueArray for the callbacks below, so they can read the
  // current array without listing it as a dependency. stableValueArray's
  // reference changes on every edit (it IS `value` when `value` is already
  // an array), so depending on it directly would give every callback below a
  // new identity per keystroke — which in turn defeats React.memo on every
  // multi-value row consumer, not just the one being edited.
  const valueArrayRef = useRef(stableValueArray);
  valueArrayRef.current = stableValueArray;

  // DnD sensors setup (shared configuration)
  const sensors = useMultiValueSensors();

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (addTimeoutRef.current) {
        clearTimeout(addTimeoutRef.current);
      }
    };
  }, []);

  // Add a specific value (business logic extracted to multiValueValidation.ts).
  // Exposed separately from handleAdd so pick-to-add editors (enumerated
  // selects) can append the chosen option directly, without round-tripping
  // through the newValue draft state.
  const handleAddValue = useCallback(
    (valueToAdd: unknown) => {
      const currentArray = valueArrayRef.current;
      const validation = validateNewValue(valueToAdd, currentArray);

      if (!validation.isValid) {
        if (validation.error) {
          setDuplicateError(validation.error);
          setAnnouncement(validation.error);
        }
        return;
      }

      // Add the value
      onChange([...currentArray, valueToAdd]);
      setNewValue(null);
      setDuplicateError(null);
      const displayValue = formatValueForText(valueToAdd);
      setAnnouncement(
        `${displayValue} added. ${currentArray.length + 1} items total.`
      );

      // Refocus input for next entry
      addTimeoutRef.current = setTimeout(() => addInputRef.current?.focus(), 0);
    },
    [onChange]
  );

  // Add the drafted value (Enter / "+" button)
  const handleAdd = useCallback(() => {
    handleAddValue(newValue);
  }, [handleAddValue, newValue]);

  // Handle Enter key
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleAdd();
      }
    },
    [handleAdd]
  );

  // Remove value
  const handleRemove = useCallback(
    (index: number) => {
      const currentArray = valueArrayRef.current;
      const removedValue = formatValueForText(currentArray[index]);
      const newArray = currentArray.filter((_, i) => i !== index);
      onChange([...newArray]);
      setAnnouncement(
        `${removedValue} removed. ${newArray.length} items remaining.`
      );
    },
    [onChange]
  );

  // Update value at index
  const handleValueChange = useCallback(
    (index: number, newVal: unknown) => {
      const newArray = [...valueArrayRef.current];
      newArray[index] = newVal;
      onChange([...newArray]);
    },
    [onChange]
  );

  // Drag handlers
  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(String(event.active.id));
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveId(null);
      const { active, over } = event;
      if (over && active.id !== over.id) {
        const currentArray = valueArrayRef.current;
        const oldIndex = Number(active.id);
        const newIndex = Number(over.id);
        const newArray = arrayMove(currentArray, oldIndex, newIndex);
        onChange([...newArray]);
        const movedValue = formatValueForText(currentArray[oldIndex]);
        setAnnouncement(`${movedValue} moved to position ${newIndex + 1}`);
      }
    },
    [onChange]
  );

  const handleDragCancel = useCallback(() => {
    setActiveId(null);
  }, []);

  const handleNewValueChange = useCallback((val: unknown) => {
    setNewValue(val);
    setDuplicateError(null);
  }, []);

  return {
    valueArray: stableValueArray,
    newValue,
    duplicateError,
    announcement,
    activeId,
    addInputRef,
    sensors,
    handleAdd,
    handleAddValue,
    handleRemove,
    handleValueChange,
    handleNewValueChange,
    handleKeyDown,
    handleDragStart,
    handleDragEnd,
    handleDragCancel,
  };
}

// =============================================================================
// MultiValuePopoverContent - Popover inner content for multi-value editors
// =============================================================================

interface MultiValuePopoverContentProps {
  propertyType: PropertyTypeSchema;
  disabled: boolean;
  /**
   * Visual density. 'popover' (default) is the roomy standalone treatment.
   * 'card' is the compact Attio-style treatment for table-cell hosts: tight
   * row rhythm, a hairline divider before the add row, a leading gutter on
   * the add row aligning it with the drag-handled value rows, per-row
   * country selectors hidden (the value's country is fixed; the dial code
   * still shows), and add/remove buttons at matching sizes.
   */
  variant?: 'popover' | 'card';
  /** Field size for every row editor and the add input. */
  size: SelectSize;
  state: UseMultiValueStateReturn;
  /** Forwarded to the add-input's nested PropertyInput for text-family leaves. */
  textDisplay?: 'form' | 'card';
}

function MultiValuePopoverContent({
  propertyType,
  disabled,
  state,
  size,
  variant = 'popover',
  textDisplay,
}: MultiValuePopoverContentProps) {
  const isCard = variant === 'card';
  const {
    valueArray,
    newValue,
    duplicateError,
    announcement,
    activeId,
    addInputRef,
    sensors,
    handleAdd,
    handleAddValue,
    handleRemove,
    handleValueChange,
    handleNewValueChange,
    handleKeyDown,
    handleDragStart,
    handleDragEnd,
    handleDragCancel,
  } = state;

  // Enumerated pickers add on pick: choosing an option IS the add — there is
  // no draft to confirm, so the value appends immediately and the "+" button
  // is dropped for these types.
  const isPickToAdd =
    propertyType.field_type_name === 'select' ||
    propertyType.field_type_name === 'boolean';

  // Scoped container for locating a row's DOM node by index after a removal
  // re-renders the list — see focusRowControl above.
  const rowListRef = useRef<HTMLDivElement>(null);

  // handleRemove itself never moves focus (see useMultiValueState above), so
  // wrap it here: after the removed row's row shifts the rest up, focus the
  // remove control of whichever row now sits at that same index (or the new
  // last row, if the removed row was the last one). If no rows remain, land
  // on the add-value input instead — there is no floor-of-1 row here.
  const handleRowRemove = useCallback(
    (index: number) => {
      const newRowCount = Math.max(0, valueArray.length - 1);
      handleRemove(index);
      setTimeout(() => {
        if (newRowCount === 0) {
          addInputRef.current?.focus();
          return;
        }
        focusRowControl(rowListRef.current, Math.min(index, newRowCount - 1));
      }, 0);
    },
    [handleRemove, valueArray.length, addInputRef]
  );

  return (
    <div className={isCard ? 'space-y-0' : 'space-y-3'}>
      {/* Aria-live region */}
      <div aria-live='polite' className='sr-only'>
        {announcement}
      </div>

      {/* No empty-state block: when there are no values, the add-input's own
          placeholder ("Type value and press Enter…") is the guidance, so a
          separate "No values yet" box would just be redundant clutter. */}

      {/* Values list (scrollable). Rows keep their full editors — including
          the phone country selector, so an existing number's country stays
          editable — just at the card's compact rhythm. */}
      <div
        ref={rowListRef}
        className={cx(
          'max-h-[40vh] overflow-y-auto',
          isCard ? 'space-y-1' : 'space-y-3'
        )}
      >
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          <SortableContext
            items={valueArray.map((_, i) => String(i))}
            strategy={verticalListSortingStrategy}
          >
            {valueArray.map((val, index) => (
              <SortableValueRow
                // eslint-disable-next-line react/no-array-index-key -- SortableContext items are index-based; changing keys would break drag-and-drop reordering
                key={index}
                id={String(index)}
                value={val}
                index={index}
                propertyType={propertyType}
                onValueChange={handleValueChange}
                onRemove={handleRowRemove}
                disabled={disabled}
                size={size}
                isLastItem={valueArray.length === 1}
                isRequired={propertyType.is_required}
                textDisplay={textDisplay}
              />
            ))}
          </SortableContext>
          <DragOverlay>
            {activeId !== null && (
              <div className='opacity-90 shadow-lg rounded-lg p-2 bg-bg-primary border border-border-primary'>
                <PropertyValueLabel
                  value={valueArray[Number(activeId)]}
                  propertyType={{ ...propertyType, is_multi: false }}
                  fontSize='base'
                />
              </div>
            )}
          </DragOverlay>
        </DndContext>
      </div>

      {/* Add new value input. Card variant separates it from the value rows
          with a hairline divider and a leading gutter that aligns its field
          with the drag-handled rows above. */}
      <div
        className={cx(
          'flex items-center gap-2',
          isCard &&
            valueArray.length > 0 &&
            'mt-1.5 border-t border-[var(--color-border-secondary)] pt-1.5'
        )}
      >
        {isCard && valueArray.length > 0 && (
          <span aria-hidden className='w-4 shrink-0' />
        )}
        {!disabled && (
          <div className='flex-1' onKeyDown={handleKeyDown}>
            <PropertyInput
              propertyType={{
                ...propertyType,
                is_multi: false,
                is_required: false,
              }}
              value={newValue as string | number | object | null | undefined}
              onChange={isPickToAdd ? handleAddValue : handleNewValueChange}
              disabled={disabled}
              size={size}
              inputRef={addInputRef}
              // Card hosts drop straight into the options for enumerated
              // pickers — the trigger is already inside an opened editor.
              autoOpenPicker={isCard && isPickToAdd}
              // Phone inputs carry their own format mask ("(555) 000-0000")
              // next to the country selector — the generic instruction would
              // replace it and read as noise.
              placeholder={
                propertyType.field_type_name === 'phone_number'
                  ? undefined
                  : 'Type value and press Enter'
              }
              textDisplay={textDisplay}
            />
          </div>
        )}
        {!disabled && !isPickToAdd && (
          <Button
            type='button'
            onPress={handleAdd}
            isDisabled={
              disabled ||
              newValue === null ||
              newValue === undefined ||
              (typeof newValue === 'string' && newValue.trim() === '') ||
              valueArray.length >= 100
            }
            aria-label={`Add ${propertyType.label.toLowerCase()}`}
            className={cx(
              'flex items-center justify-center hover:bg-bg-tertiary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring disabled:opacity-50',
              // Card variant mirrors the rows' remove button (size-6 hit
              // area, size-4 glyph) so the trailing controls line up.
              isCard ? 'w-6 h-6 rounded' : 'w-8 h-8 rounded-lg'
            )}
          >
            <Plus className={isCard ? 'w-4 h-4' : 'w-5 h-5'} />
          </Button>
        )}
      </div>

      {/* Error message */}
      {duplicateError && (
        <div className='text-sm text-error'>{duplicateError}</div>
      )}
    </div>
  );
}

// =============================================================================
// MultiValueCardEditor - Always-expanded value-list editor (card treatment)
// =============================================================================

interface MultiValueCardEditorProps {
  propertyType: PropertyTypeSchema;
  value: unknown;
  onChange: (value: unknown) => void;
  disabled: boolean;
  /** Field size for every row editor and the add input. */
  size: SelectSize;
  /** Forwarded to the add-input's nested PropertyInput for text-family leaves. */
  textDisplay?: 'form' | 'card';
}

/**
 * Renders the multi-value editor (sortable value rows + add input) directly,
 * without a trigger/popover — for hosts that are already a floating card,
 * such as a table cell's popped field (multiValueDisplay='card').
 *
 * The add input is auto-focused on mount so a double-click on the cell drops
 * the user straight into "type a value, press Enter to add".
 */
function MultiValueCardEditor({
  propertyType,
  value,
  onChange,
  disabled,
  size,
  textDisplay,
}: MultiValueCardEditorProps) {
  const state = useMultiValueState(value, onChange, propertyType);

  useEffect(() => {
    if (disabled) {
      return undefined;
    }
    const timer = setTimeout(() => state.addInputRef.current?.focus(), 0);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- focus once on mount; addInputRef is a stable ref
  }, []);

  return (
    <MultiValuePopoverContent
      propertyType={propertyType}
      disabled={disabled}
      state={state}
      size={size}
      variant='card'
      textDisplay={textDisplay}
    />
  );
}

// =============================================================================
// InlineMultiValueAdapter - Stacked inline rows, one per value
// =============================================================================

interface InlineMultiValueAdapterProps {
  propertyType: PropertyTypeSchema;
  value: unknown;
  onChange: (value: unknown) => void;
  disabled: boolean;
  trailingAddon?: React.ReactNode;
  inputRef?: React.RefObject<HTMLInputElement | null>;
  placeholder?: string;
  /** Field size for every row editor. */
  size: SelectSize;
  /** Forwarded to each row's nested PropertyInput for text-family leaves. */
  textDisplay?: 'form' | 'card';
}

/** Ceiling on values per multi-value property (mirrors the add-input guard). */
const MAX_MULTI_VALUES = 100;

interface MultiValueRowProps {
  index: number;
  value: unknown;
  propertyType: PropertyTypeSchema;
  placeholder?: string;
  disabled: boolean;
  /** Field size for this row's editor. */
  size: SelectSize;
  inputRef?: React.RefObject<HTMLInputElement | null>;
  /** Ref to the "Add another value" button, when this row renders one. */
  addButtonRef?: React.RefObject<HTMLButtonElement | null>;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  /**
   * Commit the row's draft. Fires on Enter AND on focus leaving the row, so a
   * typed value is not silently lost by clicking away — Enter-only was a real
   * dead end: the row looked filled but nothing had been saved.
   */
  onCommit?: () => void;
  showRemove: boolean;
  showAdd: boolean;
  onRowChange: (index: number, value: unknown) => void;
  onRowRemove: (index: number) => void;
  onAddRow: () => void;
  /** Forwarded to this row's nested PropertyInput for text-family leaves. */
  textDisplay?: 'form' | 'card';
}

/**
 * One value row: the scalar editor plus its trailing controls.
 *
 * Trash sits to the LEFT of "+" so the add affordance stays in the same
 * position on every row regardless of whether remove is showing. Both are
 * quiet (fg-quaternary, size-6 hit area / size-4 glyph) so they recede behind
 * the value rather than competing with it as content.
 */
const MultiValueRow = React.memo(function MultiValueRow({
  index,
  value,
  propertyType,
  placeholder,
  disabled,
  size,
  inputRef,
  addButtonRef,
  onKeyDown,
  onCommit,
  showRemove,
  showAdd,
  onRowChange,
  onRowRemove,
  onAddRow,
  textDisplay,
}: MultiValueRowProps) {
  const handleChange = useCallback(
    (newVal: unknown) => onRowChange(index, newVal),
    [index, onRowChange]
  );
  const handleRemove = useCallback(
    () => onRowRemove(index),
    [index, onRowRemove]
  );

  // React's onBlur is focusout, which bubbles — so this catches focus leaving
  // the row's input. Moving focus WITHIN the row (input -> its own trash/"+")
  // is not a commit: committing there would save a value the user is in the
  // act of discarding.
  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLDivElement>) => {
      if (!onCommit) {
        return;
      }
      if (e.currentTarget.contains(e.relatedTarget as Node | null)) {
        return;
      }
      onCommit();
    },
    [onCommit]
  );

  // Stable so it doesn't defeat this component's own React.memo wrapper on
  // every parent render — an inline spread here would recreate the object
  // (and therefore re-render the nested PropertyInput) even when nothing
  // about this row actually changed.
  const singleValuePropertyType = useMemo(
    () => ({ ...propertyType, is_multi: false }),
    [propertyType]
  );

  const label = propertyType.label.toLowerCase();
  const controlClass =
    'flex size-6 items-center justify-center rounded-md text-fg-quaternary transition-colors hover:bg-bg-tertiary hover:text-fg-secondary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring';

  const rowControls =
    disabled || (!showRemove && !showAdd) ? null : (
      <div className='flex shrink-0 items-center gap-0.5'>
        {showRemove && (
          <Button
            aria-label={`Remove ${label} ${index + 1}`}
            onPress={handleRemove}
            className={controlClass}
          >
            <Trash01 className='size-4' />
          </Button>
        )}
        {showAdd && (
          <Button
            ref={addButtonRef}
            aria-label={`Add another ${label}`}
            onPress={onAddRow}
            className={controlClass}
          >
            <Plus className='size-4' />
          </Button>
        )}
      </div>
    );

  // Captured on a wrapper rather than passed to PropertyInput, which has no
  // such props — the same approach the card editor's add-input uses for its
  // Enter-to-commit handling. data-row-index lets post-commit and
  // post-removal focus effects locate this row's DOM node by position.
  // The row controls render BESIDE the editor, not through `trailingAddon`.
  // That prop is absolutely positioned INSIDE the editor's own box (see
  // TextInput's `absolute inset-y-0 right-1.5`, plus the `pr-9` it adds so
  // text does not run under the glyphs), so every field type has to implement
  // it individually — and only text, email, phone_number, and the
  // composite editor ever did. For the other dozen the controls were silently
  // dropped and a second value was unreachable (#12301). "Inside the box on
  // the right" is also where a picker's chevron and a date field's calendar
  // glyph already sit, so those types could not have adopted it cleanly even
  // if the prop were threaded through.
  //
  // As a sibling the editor needs no knowledge of the controls at all, so
  // every field type works, including ones added later. The cost is that the
  // editor gives up the control column's width instead of overlaying it.
  return (
    <div
      data-row-index={index}
      onKeyDown={onKeyDown}
      onBlur={handleBlur}
      className='flex items-center gap-1'
    >
      <div className='min-w-0 flex-1'>
        <PropertyInput
          propertyType={singleValuePropertyType}
          value={value as string | number | object | null | undefined}
          onChange={handleChange}
          disabled={disabled}
          size={size}
          inputRef={inputRef}
          placeholder={placeholder}
          textDisplay={textDisplay}
        />
      </div>
      {rowControls}
    </div>
  );
});

/**
 * Renders one editor row per value, stacked, so a multi-value field reads and
 * behaves like the single-value fields beside it.
 *
 * Previously this rendered ONE input for `valueArray[0]` and hid the rest
 * behind a popover, and `PropertyInput` swapped the whole field to a text
 * trigger at 2+ values — so the same field had three renderings depending on
 * how many values it held, and only the first was editable in place.
 *
 * Row controls (`+` on the last row only, trash on every row once there is
 * more than one) live in each row's trailing slot, so the inputs stay
 * full-width and keep the shared `sm` size step. Field height is now a
 * multiple of one row rather than a function of the value count.
 *
 * Validation (duplicates, the 100 cap) and the a11y announcements come from
 * `useMultiValueState`, unchanged. Drag-to-reorder is NOT offered inline —
 * it remains in the card editor (`multiValueDisplay='card'`), where the rows
 * carry drag handles.
 */
function InlineMultiValueAdapter({
  propertyType,
  value,
  onChange,
  disabled,
  trailingAddon,
  inputRef,
  placeholder,
  size,
  textDisplay,
}: InlineMultiValueAdapterProps) {
  const state = useMultiValueState(value, onChange, propertyType);
  const {
    valueArray,
    newValue,
    duplicateError,
    announcement,
    addInputRef,
    handleAdd,
    handleValueChange,
    handleNewValueChange,
    handleKeyDown,
    handleRemove,
  } = state;

  // A blank row the user asked for via "+". Its draft lives in the hook's
  // `newValue` (NOT in `valueArray`) and is committed by `handleKeyDown` ->
  // `handleAdd` -> `handleAddValue` on Enter. Going through that path is what
  // keeps duplicate/max validation, the aria-live announcement and the
  // post-commit refocus working — committing straight to `onChange` per
  // keystroke bypassed all three.
  const [hasPendingRow, setHasPendingRow] = useState(false);

  const atMaxValues = valueArray.length >= MAX_MULTI_VALUES;

  // Stable targets for post-commit and post-removal refocus. addInputRef
  // (from the hook) is only attached to the DOM while the pending row is
  // mounted, so it cannot be reused as the target once that row unmounts.
  const addButtonRef = useRef<HTMLButtonElement>(null);
  const lastRowInputRef = useRef<HTMLInputElement>(null);
  const rowListRef = useRef<HTMLDivElement>(null);

  // Mirrors valueArray for handleRowChange below, so it can read the current
  // array without listing it as a dependency — valueArray is a new reference
  // on every edit, so depending on it directly would give handleRowChange (and
  // therefore every row's onRowChange prop) a new identity per keystroke,
  // defeating MultiValueRow's React.memo for every row, not just the edited one.
  const valueArrayRef = useRef(valueArray);
  valueArrayRef.current = valueArray;

  // Drop the pending row once its value lands in the array, then move focus
  // off it. addInputRef.current is null by the time a focus call fires here
  // (the pending row has already unmounted), so focus instead the "Add
  // another value" button that reappears on the new last row — or, if the
  // max-value cap was just reached and that button won't render, the newly
  // committed row's primary input. The timer is tracked in a ref (rather
  // than returned as the effect's cleanup) because `setHasPendingRow(false)`
  // here changes this same effect's own `hasPendingRow` dependency — a
  // returned cleanup would fire on that very re-render and cancel the timer
  // before it ever runs.
  const previousCount = useRef(valueArray.length);
  const commitFocusTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  useEffect(() => {
    return () => {
      if (commitFocusTimeoutRef.current) {
        clearTimeout(commitFocusTimeoutRef.current);
      }
    };
  }, []);
  useEffect(() => {
    if (hasPendingRow && valueArray.length > previousCount.current) {
      setHasPendingRow(false);
      commitFocusTimeoutRef.current = setTimeout(() => {
        if (atMaxValues) {
          lastRowInputRef.current?.focus();
        } else {
          addButtonRef.current?.focus();
        }
      }, 0);
    }
    previousCount.current = valueArray.length;
  }, [valueArray.length, hasPendingRow, atMaxValues]);

  // Focus the blank row as soon as it appears, so "+" drops the user straight
  // into typing instead of making them click the new input.
  useEffect(() => {
    if (!hasPendingRow) {
      return undefined;
    }
    const timer = setTimeout(() => addInputRef.current?.focus(), 0);
    return () => clearTimeout(timer);
  }, [hasPendingRow, addInputRef]);

  // An empty field still shows one input to type into, so the committed-row
  // count has a floor of 1. The pending row is added ON TOP of that floor —
  // folding it inside the Math.max meant that on an empty field the pending
  // row merely replaced the implicit one and "+" appeared to do nothing.
  const committedRowCount = Math.max(1, valueArray.length);
  const rowCount = committedRowCount + (hasPendingRow ? 1 : 0);
  // The pending row is always last, which is NOT valueArray.length when the
  // field is empty (floor of 1 means index 0 is the implicit committed row).
  const pendingIndex = hasPendingRow ? rowCount - 1 : -1;
  const showRemove = rowCount > 1;

  const handleRowChange = useCallback(
    (index: number, newVal: unknown) => {
      if (index === pendingIndex) {
        // A row added via "+": hold the draft until Enter commits it through
        // the validated add path.
        handleNewValueChange(newVal);
        return;
      }
      const isEmpty =
        newVal === null ||
        newVal === undefined ||
        (typeof newVal === 'string' && newVal.trim() === '');
      const currentValueArray = valueArrayRef.current;
      if (index >= currentValueArray.length) {
        // The always-present first row of an EMPTY field. This is the field's
        // primary input, not a draft — it commits as you type, exactly like a
        // single-value field, so a multi-value property doesn't silently
        // require Enter to save its first value.
        if (!isEmpty) {
          onChange([...currentValueArray, newVal]);
        }
        return;
      }
      if (isEmpty) {
        // Clearing a row drops it rather than leaving a blank entry behind.
        handleRemove(index);
        return;
      }
      handleValueChange(index, newVal);
    },
    [
      handleNewValueChange,
      handleRemove,
      handleValueChange,
      pendingIndex,
      onChange,
    ]
  );

  const handleRowRemove = useCallback(
    (index: number) => {
      if (index === pendingIndex) {
        setHasPendingRow(false);
        handleNewValueChange(null);
        return;
      }
      // Focus the analogous control on whichever row now sits at this index
      // (or the new last row, if this was the last row) once the removal has
      // re-rendered the list — handleRemove itself never moves focus. If the
      // array is now empty, target the floor-of-1 implicit row's input
      // directly rather than any remove button — a pending draft row can
      // keep the row count above 1 and render one there too.
      const willBeEmpty = valueArray.length <= 1;
      const newCommittedRowCount = Math.max(1, valueArray.length - 1);
      const targetIndex = Math.max(
        0,
        Math.min(index, newCommittedRowCount - 1)
      );
      handleRemove(index);
      setTimeout(() => {
        if (willBeEmpty) {
          focusRowInput(rowListRef.current, targetIndex);
        } else {
          focusRowControl(rowListRef.current, targetIndex);
        }
      }, 0);
    },
    [handleNewValueChange, handleRemove, pendingIndex, valueArray.length]
  );

  const handleAddRow = useCallback(() => setHasPendingRow(true), []);

  return (
    <div className='flex items-start gap-1.5'>
      <div ref={rowListRef} className='flex-1 space-y-1.5'>
        <div aria-live='polite' className='sr-only'>
          {announcement}
        </div>

        {Array.from({ length: rowCount }, (_, index) => {
          const isPending = index === pendingIndex;
          // The pending row owns the hook's add-input ref so the commit path
          // can return focus to it; the first committed row keeps the ref the
          // caller passed in. The last committed row additionally owns
          // lastRowInputRef when the max-value cap means it has no "Add
          // another value" button to fall back on for post-commit refocus.
          let rowInputRef = inputRef;
          if (isPending) {
            rowInputRef = addInputRef;
          } else if (atMaxValues && index === committedRowCount - 1) {
            rowInputRef = lastRowInputRef;
          } else if (index !== 0) {
            rowInputRef = undefined;
          }
          return (
            <MultiValueRow
              // Rows are positional: a row's identity IS its index in
              // valueArray, and each input is fully controlled by that value,
              // so there is no per-row state for a reused key to carry over.
              key={index}
              index={index}
              value={isPending ? newValue : valueArray[index]}
              propertyType={propertyType}
              placeholder={placeholder}
              disabled={disabled}
              size={size}
              inputRef={rowInputRef}
              addButtonRef={addButtonRef}
              // Enter, or focus leaving the row, commits the draft through the
              // validated add path.
              onKeyDown={isPending ? handleKeyDown : undefined}
              onCommit={isPending ? handleAdd : undefined}
              showRemove={showRemove}
              showAdd={index === rowCount - 1 && !atMaxValues}
              onRowChange={handleRowChange}
              onRowRemove={handleRowRemove}
              onAddRow={handleAddRow}
              textDisplay={textDisplay}
            />
          );
        })}

        {duplicateError && (
          <div className='text-sm text-error'>{duplicateError}</div>
        )}
      </div>
      {trailingAddon}
    </div>
  );
}

// =============================================================================
// SortableValueRow - Individual value in multi-value editor
// =============================================================================

interface SortableValueRowProps {
  id: string;
  value: unknown;
  index: number;
  propertyType: PropertyTypeSchema;
  onValueChange: (index: number, newValue: unknown) => void;
  onRemove: (index: number) => void;
  disabled: boolean;
  /** Field size for this row's editor. */
  size: SelectSize;
  isLastItem: boolean;
  isRequired: boolean;
  /** Forwarded to this row's nested PropertyInput for text-family leaves. */
  textDisplay?: 'form' | 'card';
}

function SortableValueRow({
  id,
  value,
  index,
  propertyType,
  onValueChange,
  onRemove,
  disabled,
  size,
  isLastItem,
  isRequired,
  textDisplay,
}: SortableValueRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const canRemove = !(isLastItem && isRequired);

  // Stable per-index wrappers — bind index once here so the parent can pass
  // a single stable callback reference instead of creating fresh arrow functions
  // in the .map() loop on every render.
  const handleValueChange = useCallback(
    (newVal: unknown) => onValueChange(index, newVal),
    [index, onValueChange]
  );
  const handleRemove = useCallback(() => onRemove(index), [index, onRemove]);

  return (
    <div
      ref={setNodeRef}
      data-row-index={index}
      style={style}
      className={isDragging ? 'opacity-40' : ''}
    >
      <div className='flex items-center gap-2'>
        {!disabled && (
          <button
            type='button'
            {...listeners}
            {...attributes}
            className='flex items-center justify-center touch-none rounded focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring'
            aria-label='Drag to reorder'
          >
            <GripVertical className='w-4 h-4 text-tertiary' />
          </button>
        )}
        <div className='flex-1'>
          <PropertyInput
            propertyType={{ ...propertyType, is_multi: false }}
            value={value as string | number | object | null | undefined}
            onChange={handleValueChange}
            disabled={disabled}
            size={size}
            textDisplay={textDisplay}
          />
        </div>
        {!disabled && (
          <button
            type='button'
            onClick={handleRemove}
            disabled={!canRemove}
            aria-label={
              !canRemove
                ? 'Cannot remove last required value'
                : `Remove value ${index + 1}`
            }
            className='flex items-center justify-center w-6 h-6 rounded focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring hover:text-error'
          >
            <XClose className='w-4 h-4' />
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * PropertyInput - Router component that renders the appropriate input based on field_type_name
 *
 * This component examines the propertyType.field_type_name and delegates to the appropriate
 * specialized input component. Each input component is a thin wrapper around Untitled UI components.
 *
 * Note: This component renders label-free inputs. For labeled form fields, use CrmPropertyField
 * which wraps this component with LabelWithIndicator.
 */
export const PropertyInput: React.FC<PropertyInputProps> = ({
  propertyType,
  value,
  onChange,
  disabled = false,
  trailingAddon,
  inputRef,
  placeholder,
  entityProperties,
  orgId,
  multiValueDisplay = 'inline',
  autoOpenPicker = false,
  onPickerOpenChange,
  markdownDisplay = 'form',
  initialCaretPoint,
  phoneDisplay = 'form',
  textDisplay = 'form',
  size = 'sm',
}) => {
  // Use caller-provided disabled state
  const isDisabled = disabled ?? false;

  // A multi-value SELECT is one dropdown with chips, not stacked rows. This
  // sits above the generic is_multi fan-out below because that path routes on
  // cardinality before field type, so `select` never reached its own case —
  // and the stacked-row editor delivers its add/remove controls through
  // `trailingAddon`, which a <Select> trigger has nowhere to render, leaving
  // the field with no way to add a second value at all.
  if (propertyType.is_multi && propertyType.field_type_name === 'select') {
    return (
      <MultiSelectInput
        propertyType={propertyType}
        value={value}
        onChange={onChange}
        disabled={isDisabled}
        inputRef={inputRef}
        placeholder={placeholder}
        size={size}
      />
    );
  }

  // Multi-value check — stacked inline rows (card hosts get the list editor)
  if (propertyType.is_multi) {
    // Composite multi-value (with subproperties) always uses popover
    if (propertyType.subproperties?.length) {
      return (
        <CompositeMultiValueEditor
          propertyType={propertyType}
          value={value as unknown}
          onChange={onChange}
          disabled={isDisabled}
          trailingAddon={trailingAddon}
        />
      );
    }
    // Card hosts (a table cell's popped field) render the full value-list
    // editor directly — the host already provides the floating container, so
    // the trigger/popover indirection of the inline adapter would nest a
    // popover inside a card (double chrome, cramped single-line editor).
    if (multiValueDisplay === 'card') {
      return (
        <MultiValueCardEditor
          propertyType={propertyType}
          value={value}
          onChange={onChange}
          disabled={isDisabled}
          size={size}
          textDisplay={textDisplay}
        />
      );
    }
    // One rendering for every count: stacked rows, one per value. Previously
    // this branched to a text-trigger + popover at 2+ values, so the field
    // changed component (and height) the moment a second value was added.
    return (
      <InlineMultiValueAdapter
        propertyType={propertyType}
        value={value}
        onChange={onChange}
        disabled={isDisabled}
        trailingAddon={trailingAddon}
        inputRef={inputRef}
        placeholder={placeholder}
        size={size}
        textDisplay={textDisplay}
      />
    );
  }

  switch (propertyType.field_type_name) {
    case 'text':
      return (
        <TextInput
          propertyType={propertyType}
          value={value}
          onChange={onChange}
          disabled={isDisabled}
          inputRef={inputRef}
          placeholder={placeholder}
          trailingAddon={trailingAddon}
          size={size}
          textDisplay={textDisplay}
        />
      );

    case 'email':
      return (
        <EmailInput
          propertyType={propertyType}
          value={value}
          onChange={onChange}
          disabled={isDisabled}
          inputRef={inputRef}
          placeholder={placeholder}
          trailingAddon={trailingAddon}
          size={size}
        />
      );

    case 'phone_number': // Match backend field_type_name
      return (
        <PhoneInput
          propertyType={propertyType}
          value={value}
          onChange={onChange}
          disabled={isDisabled}
          trailingAddon={trailingAddon}
          inputRef={inputRef}
          placeholder={placeholder}
          display={phoneDisplay}
          size={size}
        />
      );

    case 'percentage':
    case 'number':
      return (
        <NumberInput
          propertyType={propertyType}
          value={value}
          onChange={onChange}
          disabled={isDisabled}
          inputRef={inputRef}
          placeholder={placeholder}
          size={size}
        />
      );

    case 'date':
      return (
        <DateInput
          propertyType={propertyType}
          value={value}
          onChange={onChange}
          disabled={isDisabled}
          inputRef={inputRef}
          placeholder={placeholder}
          autoOpenPicker={autoOpenPicker}
          onPickerOpenChange={onPickerOpenChange}
          size={size}
        />
      );

    case 'datetime':
      return (
        <DateTimeInput
          propertyType={propertyType}
          value={value}
          onChange={onChange}
          disabled={isDisabled}
          placeholder={placeholder}
          autoOpenPicker={autoOpenPicker}
          onPickerOpenChange={onPickerOpenChange}
          size={size}
        />
      );

    case 'boolean':
      return (
        <SelectInput
          propertyType={propertyType}
          value={value}
          onChange={onChange}
          disabled={isDisabled}
          inputRef={inputRef}
          placeholder={placeholder}
          autoOpenPicker={autoOpenPicker}
          size={size}
        />
      );

    case 'select':
      return (
        <SelectInput
          propertyType={propertyType}
          value={value}
          onChange={onChange}
          disabled={isDisabled}
          inputRef={inputRef}
          placeholder={placeholder}
          autoOpenPicker={autoOpenPicker}
          size={size}
        />
      );

    case 'timezone':
      return (
        <TimezoneInput
          propertyType={propertyType}
          value={value}
          onChange={onChange}
          disabled={isDisabled}
          inputRef={inputRef}
          placeholder={placeholder}
          autoOpenPicker={autoOpenPicker}
          size={size}
        />
      );

    case 'language':
      return (
        <LanguageInput
          propertyType={propertyType}
          value={value}
          onChange={onChange}
          disabled={isDisabled}
          inputRef={inputRef}
          placeholder={placeholder}
          size={size}
        />
      );

    case 'domain':
      return (
        <DomainInput
          propertyType={propertyType}
          value={value}
          onChange={onChange}
          disabled={isDisabled}
          placeholder={placeholder}
          size={size}
        />
      );

    case 'url':
      return (
        <UrlInput
          propertyType={propertyType}
          value={value}
          onChange={onChange}
          disabled={isDisabled}
          inputRef={inputRef}
          placeholder={placeholder}
          size={size}
        />
      );

    case 'currency':
      return (
        <CurrencyInput
          propertyType={propertyType}
          value={value}
          onChange={onChange}
          disabled={isDisabled}
          inputRef={inputRef}
          placeholder={placeholder}
          size={size}
        />
      );

    case 'multi_currency':
      return (
        <MultiCurrencyInput
          propertyType={propertyType}
          value={value}
          onChange={onChange}
          disabled={isDisabled}
          inputRef={inputRef}
          placeholder={placeholder}
        />
      );

    case 'textarea':
      return (
        <TextAreaInput
          propertyType={propertyType}
          value={value}
          onChange={onChange}
          disabled={isDisabled}
          placeholder={placeholder}
          size={size}
        />
      );

    case 'markdown':
      return (
        <MarkdownInput
          propertyType={propertyType}
          value={value}
          onChange={onChange}
          disabled={isDisabled}
          placeholder={placeholder}
          display={markdownDisplay}
          initialCaretPoint={initialCaretPoint}
        />
      );

    case 'manifest':
      // Manifests are system-managed and read-only; render as display-only
      return (
        <div className='text-sm text-tertiary italic px-3 py-2'>
          Manifest (read-only)
        </div>
      );

    case 'ahoy_report_query':
      // Report queries are authored by the agent (via the query_report tool +
      // chart_title) and validated by the field type at write time. The
      // proposal preview surfaces them as a read-only JSON block so the user
      // can scan the agent's work before approving; edits happen by asking
      // the agent to refine, not by hand-editing JSON in the form.
      return (
        <pre className='text-xs font-mono text-fg-secondary bg-bg-secondary rounded p-3 max-h-64 overflow-auto whitespace-pre-wrap break-words'>
          {value && typeof value === 'object'
            ? JSON.stringify(value, null, 2)
            : (value ?? '—')}
        </pre>
      );

    case 'formula':
      return (
        <div className='rounded p-3 bg-bg-secondary max-h-64 overflow-auto'>
          <PropertyValueLabel
            value={value}
            propertyType={propertyType}
            orgId={orgId}
            entityProperties={entityProperties}
          />
        </div>
      );

    case 'permissions':
      // Permission grants are write-once (`value_update_access: none`), so the
      // edit form shows them read-only rather than offering an input the
      // backend would reject. Same treatment as `formula` above.
      return (
        <div className='rounded p-3 bg-bg-secondary max-h-64 overflow-auto'>
          <PropertyValueLabel
            value={value}
            propertyType={propertyType}
            orgId={orgId}
            entityProperties={entityProperties}
          />
        </div>
      );

    case 'permission_group':
      // Permission groups are system-managed references; render as display-only
      return (
        <div className='text-sm text-tertiary italic px-3 py-2'>
          {value ? String(value) : '—'}
        </div>
      );

    case 'org_type_name':
      return (
        <TextInput
          propertyType={propertyType}
          value={value}
          onChange={onChange}
          disabled={isDisabled}
          inputRef={inputRef}
          placeholder={placeholder}
          size={size}
          textDisplay={textDisplay}
        />
      );

    case 'event_type':
      return (
        <SelectInput
          propertyType={propertyType}
          value={value}
          onChange={onChange}
          disabled={isDisabled}
          inputRef={inputRef}
          placeholder={placeholder}
          size={size}
        />
      );

    case 'entity_type':
      return (
        <EntityTypeSelectInput
          propertyType={propertyType}
          value={value}
          onChange={onChange}
          disabled={isDisabled}
          inputRef={inputRef}
          placeholder={placeholder}
          size={size}
        />
      );

    case 'association_type':
      return (
        <AssociationTypeSelectInput
          propertyType={propertyType}
          value={value}
          onChange={onChange}
          disabled={isDisabled}
          inputRef={inputRef}
          placeholder={placeholder}
          size={size}
        />
      );

    case 'field_type':
      return (
        <FieldTypeSelectInput
          propertyType={propertyType}
          value={value}
          onChange={onChange}
          disabled={isDisabled}
          inputRef={inputRef}
          placeholder={placeholder}
          size={size}
        />
      );

    case 'property_type':
      return (
        <PropertyTypeSelectInput
          propertyType={propertyType}
          value={value}
          onChange={onChange}
          disabled={isDisabled}
          inputRef={inputRef}
          placeholder={placeholder}
          size={size}
        />
      );

    case 'icon':
      return (
        <IconSelectInput
          propertyType={propertyType}
          value={value}
          onChange={onChange}
          disabled={isDisabled}
          placeholder={placeholder}
        />
      );

    case 'color':
      return (
        <ColorSelectInput
          propertyType={propertyType}
          value={value}
          onChange={onChange}
          disabled={isDisabled}
          placeholder={placeholder}
        />
      );

    case 'working_hours_schedule':
      return (
        <WorkingHoursInput
          propertyType={propertyType}
          value={value}
          onChange={onChange}
          disabled={isDisabled}
          inputRef={inputRef}
          placeholder={placeholder}
          size={size}
        />
      );

    case 'booking_questions':
      return (
        <BookingQuestionsInput
          propertyType={propertyType}
          value={value}
          onChange={onChange}
          disabled={isDisabled}
          inputRef={inputRef}
          placeholder={placeholder}
          size={size}
        />
      );

    case 'location_type':
      return (
        <LocationTypeInput
          propertyType={propertyType}
          value={value}
          onChange={onChange}
          disabled={isDisabled}
          inputRef={inputRef}
          placeholder={placeholder}
          size={size}
        />
      );

    case 'web_source':
      return (
        <TextInput
          propertyType={propertyType}
          value={value}
          onChange={onChange}
          disabled={isDisabled}
          inputRef={inputRef}
          placeholder={placeholder}
          size={size}
          textDisplay={textDisplay}
        />
      );

    default:
      if (process.env.NODE_ENV !== 'production') {
        console.warn(
          `[PropertyInput] Unknown field_type_name: "${propertyType.field_type_name}". ` +
            `Check that the schema for "${propertyType.property_type_name}" uses a registered field type.`
        );
      }
      return (
        <div className='p-3 bg-utility-error-50 border border-utility-error-200 rounded-lg'>
          <p className='text-sm text-utility-error-700'>
            Unknown field type:{' '}
            <code className='font-mono'>{propertyType.field_type_name}</code>
          </p>
        </div>
      );
  }
};
